import cx from "classnames";
import { inject, observer } from "mobx-react";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaArrowRight,
  FaEnvelope,
  FaRedo,
  FaSignInAlt,
  FaSms,
} from "react-icons/fa";
import { Modal } from "reactstrap";
import Button from "../../../components/Button";
import { MessageResultState } from "../../../types/general-types";
import LoginStore from "./store";
import { LoginRequestDto, VerificationTypes } from "./types";

interface IPage {
  LoginStore?: LoginStore;
  clientId: string | null;
  loginRequestDto: LoginRequestDto;
  isShow: boolean;
  toggleModal: any;
}

const VerifyPage: FC<IPage> = inject("LoginStore")(
  observer((props: IPage) => {
    const {
      LoginStore: store,
      clientId,
      loginRequestDto,
      toggleModal,
      isShow,
    } = props;
    const { t } = useTranslation();
    const {
      register,
      formState,
      handleSubmit,
      errors,
      control,
      clearErrors,
      setValue,
      getValues,
    } = useForm();

    const [timer, setTimer] = useState<number>();
    const [verificationType, setVerificationType] =
      useState<VerificationTypes>();
    const [step, setStep] = useState<"verificationType" | "verificationCode">(
      "verificationType"
    );
    const verificationCodeRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
      let interval: any = null;
      if (timer && timer > 0) {
        interval = setInterval(() => {
          setTimer((prev) => (prev ? prev - 1 : 0));
        }, 1000); // Her saniyede bir sayacı bir azalt
      } else {
        setValue("verificationCode", "");
        clearInterval(interval); // Zamanlayıcıyı durdur
      }

      return () => clearInterval(interval); // Component unmount olduğunda zamanlayıcıyı temizle
    }, [setValue, timer]);

    const sendVerificationCode = useCallback(async () => {
      if (verificationType && store && loginRequestDto) {
        await store
          ?.SendVerificationCode({
            username: loginRequestDto.username,
            password: loginRequestDto.password,
            verificationType: verificationType,
          })
          .then((res) => {
            if (res.state === MessageResultState.SUCCESS) {
              setValue("verificationCode", "");
              setStep("verificationCode");
              setTimer(res.result.verificationSeconds);
            }
          });
      }
    }, [loginRequestDto, setValue, store, verificationType]);
    useEffect(() => {
      if (step === "verificationCode" && isShow) {
        setTimeout(() => {
          if (verificationCodeRef.current) {
            verificationCodeRef.current.focus();
          }
        }, 100);
      }
    }, [step, isShow]);

    const checkVerificationCode = useCallback(async () => {
      if (
        verificationType &&
        store &&
        loginRequestDto &&
        getValues("verificationCode")
      ) {
        await store
          ?.CheckVerificationCode({
            username: loginRequestDto.username,
            password: loginRequestDto.password,
            verificationType: verificationType,
            verificationCode: getValues("verificationCode"),
            clientId: clientId,
          })
          .then((response) => {
            if (response.state === MessageResultState.SUCCESS) {
              toggleModal(response.result);
            }
          });
      }
    }, [
      clientId,
      getValues,
      loginRequestDto,
      store,
      toggleModal,
      verificationType,
    ]);

    const VerificationTypePage = useMemo(() => {
      return (
        <>
          <div className="d-flex flex-column align-items-center justify-content-center py-4">
            <div
              className="card w-100 shadow-lg border-0 "
              style={{ maxWidth: "400px", borderRadius: "15px" }}
            >
              <div className="card-body">
                <div className="card-title mb-4 fw-semibold d-flex justify-content-between align-items-center">
                  <h5 className="text-center w-100 m-0">
                    {t("Verification Type")}{" "}
                    <span className="text-danger">*</span>
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => toggleModal()}
                  ></button>
                </div>

                {/* SMS */}
                <div className="form-check mb-3">
                  <input
                    type="radio"
                    name="verificationTypeSms"
                    id="verificationTypeSms"
                    className={"form-check-input"}
                    checked={verificationType === VerificationTypes.Sms}
                    onChange={() => setVerificationType(VerificationTypes.Sms)}
                    style={{ transform: "scale(1.2)" }}
                    disabled={formState.isSubmitting}
                  />
                  <label
                    className="form-check-label align-items-center ms-2"
                    htmlFor="verificationTypeSms"
                    style={{
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "500",
                      color:
                        verificationType === VerificationTypes.Sms
                          ? "#007bff"
                          : "#555",
                    }}
                  >
                    <FaSms className="me-2" style={{ fontSize: "18px" }} />
                    {t("Sms")}
                  </label>
                </div>

                {/* E-Posta */}
                <div className="form-check mb-4">
                  <input
                    type="radio"
                    name="verificationTypeEmail"
                    id="verificationTypeEmail"
                    className="form-check-input"
                    checked={verificationType === VerificationTypes.Email}
                    onChange={() =>
                      setVerificationType(VerificationTypes.Email)
                    }
                    style={{ transform: "scale(1.2)" }}
                    disabled={formState.isSubmitting}
                  />
                  <label
                    className="form-check-label align-items-center ms-2"
                    htmlFor="verificationTypeEmail"
                    style={{
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "500",
                      color:
                        verificationType === VerificationTypes.Email
                          ? "#007bff"
                          : "#555",
                      transition: "color 0.3s ease",
                    }}
                  >
                    <FaEnvelope className="me-2" style={{ fontSize: "18px" }} />
                    {t("Email")}
                  </label>
                </div>

                <div className="text-center">
                  <Button
                    className="btn btn-primary align-items-center"
                    onClick={handleSubmit(async () => {
                      await sendVerificationCode();
                    })}
                    disabled={!verificationType}
                    icon={<FaArrowRight className="me-2" />}
                    text={t("Next")}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }, [
      formState.isSubmitting,
      handleSubmit,
      sendVerificationCode,
      t,
      toggleModal,
      verificationType,
    ]);

    const VerificationCodePage = useMemo(() => {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center p-0">
          <div
            className="card w-100 shadow-lg border-0 "
            style={{ maxWidth: "400px", borderRadius: "15px" }}
          >
            <div className="card-body">
              <div className="card-title mb-4 fw-semibold d-flex justify-content-between align-items-center">
                <h5 className="text-center w-100 m-0">
                  {t("Verification Code")}{" "}
                  <span className="text-danger">*</span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => toggleModal()}
                ></button>
              </div>

              {/* Doğrulama Kodu Input */}
              <div className="mb-4 d-flex justify-content-center">
                <Controller
                  name="verificationCode"
                  control={control}
                  rules={{
                    required: t("Please fill out this field."),
                    minLength: {
                      value: 4,
                      message: t(
                        "The given value must be {{value}} characters..",
                        { value: 4 }
                      ),
                    },
                    maxLength: {
                      value: 8,
                      message: t(
                        "The given value must be {{value}} characters.",
                        { value: 8 }
                      ),
                    },
                  }}
                  render={(field, state) => (
                    <>
                      <input
                        minLength={4}
                        maxLength={8}
                        type="text"
                        placeholder={t("Verification Code")}
                        style={{ width: "50%" }}
                        disabled={formState.isSubmitting || timer === 0}
                        {...field}
                        className={cx("form-control text-center ", {
                          "is-invalid": errors["verificationCode"],
                        })}
                        ref={verificationCodeRef}
                        onKeyDown={(
                          event: React.KeyboardEvent<HTMLInputElement>
                        ) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleSubmit(async () => {
                              await checkVerificationCode();
                            })();
                          }
                        }}
                      />
                    </>
                  )}
                />
              </div>

              {/* Zamanlayıcı */}
              <div className="text-center mb-3">
                {timer && timer > 0 ? (
                  <span>
                    {t("Remaining Time:")} {timer} {t("seconds")}
                  </span>
                ) : (
                  <Button
                    className="btn btn-warning align-items-center"
                    onClick={sendVerificationCode}
                    icon={<FaRedo />}
                    tooltipText={t("Resend Code")}
                  />
                )}
              </div>

              <div className="text-center pt-4">
                <Button
                  className="btn btn-primary align-items-center me-4"
                  onClick={async () => {
                    setStep("verificationType");
                  }}
                  icon={<FaArrowLeft className="me-2" />}
                  text={t("Back")}
                  disabled={timer && timer > 0 ? true : false}
                  id="backButton"
                  name="backButton"
                />
                <Button
                  className="btn btn-success align-items-center ms-4"
                  // onClick={() => {
                  //   console.log("errors", errors);
                  //   handleSubmit(async () => {
                  //     setStep("verificationType");
                  //   });
                  // }}
                  onClick={handleSubmit(async () => {
                    await checkVerificationCode();
                  })}
                  icon={<FaSignInAlt className="me-2" />}
                  text={t("Verify")}
                  disabled={timer === 0 || formState.isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }, [
      checkVerificationCode,
      control,
      errors,
      formState.isSubmitting,
      handleSubmit,
      sendVerificationCode,
      t,
      timer,
      toggleModal,
    ]);

    return (
      <Modal
        isOpen={isShow}
        toggle={() => toggleModal()}
        className="verify-modal"
      >
        <div>
          {step === "verificationType"
            ? VerificationTypePage
            : VerificationCodePage}
        </div>
      </Modal>
    );
  })
);
export default VerifyPage;
